import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'anihis-dental-image-dog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCheckboxModule,
    MatTooltipModule,
    TranslocoModule,
  ],
  templateUrl: './dental-image-dog.component.html',
  styleUrl: './dental-image-dog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DentalImageDogComponent {
  @Output() stateReport = new EventEmitter<any>();
  selectedOption = '';
  options = [
    { value: 'cleaningUpper', viewValue: 'cleaningUpper', checked: false },
    { value: 'cleaningLower', viewValue: 'cleaningLower', checked: false },
    { value: 'dentureUpper', viewValue: 'dentureUpper', checked: false },
    { value: 'dentureLower', viewValue: 'dentureLower', checked: false },
  ];

  updateToothState(toothNumber: number): void {
    const toothElement = document.querySelector(`.tooth-${toothNumber}`);
    if (!toothElement) {
      return;
    }

    // 1) Read local flags BEFORE any removal.
    const wasExtracted = toothElement.classList.contains('active');
    const wasRepaired = toothElement.classList.contains('repair-tooth-active');

    // 2) Read global options for cleaning and denture (prosthesis)
    const isUpper = toothNumber < 300;
    let isCleaning = false;
    let isProsthesis = false;
    if (isUpper) {
      const cleaningUpper = this.options.find(
        (o) => o.value === 'cleaningUpper',
      );
      isCleaning = cleaningUpper ? cleaningUpper.checked : false;
      const dentureUpper = this.options.find((o) => o.value === 'dentureUpper');
      isProsthesis = dentureUpper ? dentureUpper.checked : false;
    } else {
      const cleaningLower = this.options.find(
        (o) => o.value === 'cleaningLower',
      );
      isCleaning = cleaningLower ? cleaningLower.checked : false;
      const dentureLower = this.options.find((o) => o.value === 'dentureLower');
      isProsthesis = dentureLower ? dentureLower.checked : false;
    }

    // 3) Remove all old state classes.
    const allStates = [
      'active', // extracted (red)
      'repair-tooth-active', // repair (green)
      'cleaning-tooth-active', // cleaning (yellow)
      'prosthesis-active', // prosthesis (blue)
      'prosthesis-cleaning-active', // prosthesis + cleaning (orange)
      'prosthesis-cleaning-repair-tooth-active', // triple combo (purple)
    ];
    allStates.forEach((stateClass) =>
      toothElement.classList.remove(stateClass),
    );

    // 4) Decide final state class based on priority.
    let finalClass = '';
    if (wasExtracted) {
      finalClass = 'active'; // extracted = red
    } else if (wasRepaired) {
      // Lock in repair: if the tooth was repaired, we want it to remain in repair state.
      finalClass = 'repair-tooth-active'; // repair = green
    } else {
      // Otherwise, use global options.
      if (isProsthesis && isCleaning) {
        finalClass = 'prosthesis-cleaning-active'; // combined = orange
      } else if (isProsthesis) {
        finalClass = 'prosthesis-active'; // prosthesis = blue
      } else if (isCleaning) {
        finalClass = 'cleaning-tooth-active'; // cleaning = yellow
      } else {
        finalClass = ''; // default (white)
      }
    }

    // 5) Preserve the base tooth classes (for example, "tooth-201")
    const baseClasses = Array.from(toothElement.classList)
      .filter((cls) => cls.startsWith('tooth-'))
      .join(' ');

    // 6) Keep any extra classes (tooltips, etc.)
    const defaultExtra = `mat-mdc-tooltip-trigger cls-1 tooth .tooth-${toothNumber}`;

    // 7) Build and set the new class string.
    const newClassString = finalClass
      ? `${baseClasses} ${defaultExtra} ${finalClass}`
      : `${baseClasses} ${defaultExtra}`;
    toothElement.setAttribute('class', newClassString.trim());

    // Report the states of all teeth after this update.
    this.reportStates();
  }

  handlePathClick(toothNumber: number): void {
    const toothElement = document.querySelector(`.tooth-${toothNumber}`);
    if (!toothElement) return;

    if (toothElement.classList.contains('active')) {
      toothElement.classList.remove('active');
    } else {
      // Remove repair state if present, then add extraction.
      toothElement.classList.remove('repair-tooth-active');
      toothElement.classList.add('active');
    }
    this.updateToothState(toothNumber);
  }

  onRightClick(event: MouseEvent, toothNumber: number): void {
    event.preventDefault();
    const toothElement = document.querySelector(`.tooth-${toothNumber}`);
    if (!toothElement) return;

    if (toothElement.classList.contains('repair-tooth-active')) {
      toothElement.classList.remove('repair-tooth-active');
    } else {
      toothElement.classList.remove('active');
      toothElement.classList.add('repair-tooth-active');
    }
    this.updateToothState(toothNumber);
  }

  /**
   * Global handlers:
   * For cleaning and denture (prosthesis) toggles, recalc the state for each tooth.
   */
  handleCleaningUpper(): void {
    const toothElements = document.querySelectorAll(
      '.tooth-101, .tooth-102, .tooth-103, .tooth-104, .tooth-105, .tooth-106, ' +
        '.tooth-107, .tooth-108, .tooth-109, .tooth-110, ' +
        '.tooth-201, .tooth-202, .tooth-203, .tooth-204, .tooth-205, .tooth-206, ' +
        '.tooth-207, .tooth-208, .tooth-209, .tooth-210',
    );
    toothElements.forEach((element: Element) => {
      const toothNum = this.extractToothNumber(element);
      this.updateToothState(toothNum);
    });
  }

  handleCleaningLower(): void {
    const toothElements = document.querySelectorAll(
      '.tooth-301, .tooth-302, .tooth-303, .tooth-304, .tooth-305, .tooth-306, ' +
        '.tooth-307, .tooth-308, .tooth-309, .tooth-310, .tooth-311, ' +
        '.tooth-401, .tooth-402, .tooth-403, .tooth-404, .tooth-405, .tooth-406, ' +
        '.tooth-407, .tooth-408, .tooth-409, .tooth-410, .tooth-411',
    );
    toothElements.forEach((element: Element) => {
      const toothNum = this.extractToothNumber(element);
      this.updateToothState(toothNum);
    });
  }

  handleDentureUpper(): void {
    const toothElements = document.querySelectorAll(
      '.tooth-101, .tooth-102, .tooth-103, .tooth-104, .tooth-105, .tooth-106, ' +
        '.tooth-107, .tooth-108, .tooth-109, .tooth-110, ' +
        '.tooth-201, .tooth-202, .tooth-203, .tooth-204, .tooth-205, .tooth-206, ' +
        '.tooth-207, .tooth-208, .tooth-209, .tooth-210',
    );
    toothElements.forEach((element: Element) => {
      const toothNum = this.extractToothNumber(element);
      this.updateToothState(toothNum);
    });
  }

  handleDentureLower(): void {
    const toothElements = document.querySelectorAll(
      '.tooth-301, .tooth-302, .tooth-303, .tooth-304, .tooth-305, .tooth-306, ' +
        '.tooth-307, .tooth-308, .tooth-309, .tooth-310, .tooth-311, ' +
        '.tooth-401, .tooth-402, .tooth-403, .tooth-404, .tooth-405, .tooth-406, ' +
        '.tooth-407, .tooth-408, .tooth-409, .tooth-410, .tooth-411',
    );
    toothElements.forEach((element: Element) => {
      const toothNum = this.extractToothNumber(element);
      this.updateToothState(toothNum);
    });
  }

  /**
   * Helper: Extracts the tooth number from a class that starts with "tooth-".
   */
  private extractToothNumber(element: Element): number {
    const match = Array.from(element.classList)
      .find((cls) => cls.startsWith('tooth-'))
      ?.split('-')[1];
    return match ? parseInt(match, 10) : 0;
  }

  reportStates(): void {
    // Query all elements that include a "tooth-" class.
    const allTeeth = document.querySelectorAll('[class*="tooth-"]');

    // Initialize the result object with separate arrays.
    const result: { [state: string]: number[] } = {
      active: [], // extracted
      'repair-tooth-active': [], // repair
      'cleaning-tooth-active': [], // cleaning
      'prosthesis-active': [], // prosthesis/denture
    };

    allTeeth.forEach((el: Element) => {
      const toothNum = this.extractToothNumber(el);
      if (!toothNum) return;

      // Get the element's class list as an array.
      const classes = Array.from(el.classList);

      // Determine global cleaning and prosthesis options based on tooth number.
      const isUpper = toothNum < 300;
      let isCleaningGlobal = false;
      let isProsthesisGlobal = false;
      if (isUpper) {
        const cleaningUpper = this.options.find(
          (o) => o.value === 'cleaningUpper',
        );
        isCleaningGlobal = cleaningUpper ? cleaningUpper.checked : false;
        const dentureUpper = this.options.find(
          (o) => o.value === 'dentureUpper',
        );
        isProsthesisGlobal = dentureUpper ? dentureUpper.checked : false;
      } else {
        const cleaningLower = this.options.find(
          (o) => o.value === 'cleaningLower',
        );
        isCleaningGlobal = cleaningLower ? cleaningLower.checked : false;
        const dentureLower = this.options.find(
          (o) => o.value === 'dentureLower',
        );
        isProsthesisGlobal = dentureLower ? dentureLower.checked : false;
      }

      // If extracted, report only in "active"
      if (classes.includes('active')) {
        result['active'].push(toothNum);
      } else {
        // Report repair state.
        if (
          classes.includes('repair-tooth-active') ||
          classes.includes('prosthesis-cleaning-repair-tooth-active')
        ) {
          result['repair-tooth-active'].push(toothNum);
        }
        // Report cleaning state.
        if (
          classes.includes('cleaning-tooth-active') ||
          classes.includes('prosthesis-cleaning-active') ||
          classes.includes('prosthesis-cleaning-repair-tooth-active') ||
          (classes.includes('repair-tooth-active') && isCleaningGlobal)
        ) {
          result['cleaning-tooth-active'].push(toothNum);
        }
        // Report prosthesis state.
        if (
          classes.includes('prosthesis-active') ||
          classes.includes('prosthesis-cleaning-active') ||
          classes.includes('prosthesis-cleaning-repair-tooth-active') ||
          (classes.includes('repair-tooth-active') && isProsthesisGlobal)
        ) {
          result['prosthesis-active'].push(toothNum);
        }
      }
    });

    this.stateReport.emit(result);
  }

  getSelectedOptions(): string[] {
    return this.options
      .filter((option) => option.checked)
      .map((option) => option.value);
  }

  onOptionChange(value: string) {
    switch (value) {
      case 'cleaningUpper':
        this.handleCleaningUpper();
        break;
      case 'cleaningLower':
        this.handleCleaningLower();
        break;
      case 'dentureUpper':
        this.handleDentureUpper();
        break;
      case 'dentureLower':
        this.handleDentureLower();
        break;
      default:
        break;
    }
  }
}
